<template>
  <main class="landing-hero">
    <div class="hero-content">
      <h1 class="karaoke-headline" aria-label="Sing any song in karaoke">
        <span class="karaoke-line" aria-hidden="true">
          <span class="karaoke-word karaoke-word--1">Sing</span>
          <span class="karaoke-word karaoke-word--2">any</span>
          <span class="karaoke-word karaoke-word--3">song</span>
          <span class="karaoke-word karaoke-word--4">in</span>
          <span class="karaoke-word karaoke-word--5">karaoke</span>
        </span>
      </h1>

      <div class="karaoke-progress" aria-hidden="true">
        <div class="karaoke-progress-fill" aria-hidden="true"></div>
      </div>

      <button
        type="button"
        @click="createRoom"
        :disabled="isCreating"
        :aria-busy="isCreating"
        :aria-label="isCreating ? 'Creating room' : 'Start Singing'"
        class="start-button"
      >
        <span v-if="!isCreating">Start Singing</span>
        <span v-else class="loading-spinner" aria-hidden="true"></span>
      </button>

      <button
        v-if="!showJoinRoom"
        type="button"
        class="join-room-toggle"
        :aria-expanded="showJoinRoom"
        aria-controls="join-room-form"
        @click="showJoinRoomForm"
      >or Join Room</button>

      <form v-if="showJoinRoom" id="join-room-form" class="join-room-form" @submit.prevent="joinRoom">
        <input
          ref="joinRoomInput"
          v-model="joinRoomId"
          type="text"
          class="join-room-input"
          aria-label="Room ID"
          placeholder="Enter room ID"
          autocomplete="off"
          autocapitalize="characters"
          enterkeyhint="go"
        />
        <button type="submit" class="join-room-button">Join</button>
      </form>

      <p v-if="joinRoomError" class="error-message join-room-error" role="alert">
        {{ joinRoomError }}
      </p>

      <p v-if="error" class="error-message" role="alert">{{ error }}</p>
    </div>
  </main>

    <!-- Consent Dialog -->
    <div v-if="showConsentDialog" class="modal-overlay" @click="cancelConsent">
      <div class="modal-content consent-modal" @click.stop>
        <button class="modal-close" @click="cancelConsent">×</button>
        <h2>🎤 Before We Start</h2>
        
        <div class="consent-notice">
          <p>This app uses <strong>YouTube's API Services</strong> to provide karaoke functionality.</p>
          
          <div class="terms-box">
            <div class="terms-icon">📺</div>
            <div class="terms-text">
              By using this app, you agree to <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube's Terms of Service</a>
            </div>
          </div>
          
          <div class="terms-box">
            <div class="terms-icon">📋</div>
            <div class="terms-text">
              We collect and store data as described in our <router-link to="/privacy">Privacy Policy</router-link>
            </div>
          </div>
        </div>

        <label class="consent-checkbox-container">
          <input type="checkbox" v-model="consentChecked" class="consent-checkbox-input" />
          <span class="consent-checkbox-label">I have read and agree to the terms above</span>
        </label>

        <div class="modal-actions">
          <button @click="cancelConsent" class="cancel-button">Cancel</button>
          <button @click="acceptConsentAndCreateRoom" class="accept-button" :disabled="!consentChecked">
            <span v-if="!isCreating">Accept & Create Room</span>
            <span v-else class="loading-spinner"></span>
          </button>
        </div>
      </div>
    </div>

</template>

<script>
import { ref } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../firebase'
import { useRouter } from 'vue-router'
import { useAnalytics } from '../composables/useAnalytics'

export default {
  name: 'LandingPage',
  setup() {
    const router = useRouter()
    const isCreating = ref(false)
    const showConsentDialog = ref(false)
    const { trackEvent } = useAnalytics()
    return { router, isCreating, showConsentDialog, trackEvent }
  },
  data() {
    return {
      error: '',
      consentChecked: false,
      showJoinRoom: false,
      joinRoomId: '',
      joinRoomError: ''
    }
  },
  methods: {
    async createRoom() {
      if (this.isCreating) return
      this.showConsentDialog = true
    },
    cancelConsent() {
      this.showConsentDialog = false;
    },
    showJoinRoomForm() {
      this.showJoinRoom = true
      this.$nextTick(() => this.$refs.joinRoomInput?.focus())
    },
    joinRoom() {
      const roomId = this.joinRoomId.trim().toUpperCase()

      if (!roomId) {
        this.joinRoomError = 'Enter a room ID.'
        return
      }

      this.joinRoomError = ''
      this.trackEvent('join_room_submitted', { room_id: roomId })
      this.router.push({ name: 'SessionClient', params: { roomId } })
    },
    async acceptConsentAndCreateRoom() {
      this.showConsentDialog = false;
      this.isCreating = true;
      this.error = '';
      
      try {
        const generateRoomId = (length = 6) => {
          const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
          let result = '';
          for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        };

        const roomId = generateRoomId().toUpperCase();
        const createRoomFn = httpsCallable(functions, 'createRoomV2');
        
        await createRoomFn({
          roomId,
          initialData: {
            currentVideo: 'jNQXAC9IVRw',
            status: 'active'
          }
        });
        
        // Store consent in localStorage
        localStorage.setItem('youtubeApiConsent', 'accepted');
        
        // Log room creation success
        this.trackEvent('room_created', {
          room_id: roomId
        });
        this.router.push(`/room/${roomId}`);
      } catch (error) {
        console.error('Error creating room:', error);
        this.error = 'Failed to create room. Please try again.';
        
        // Log room creation error
        this.trackEvent('room_creation_error', {
          error_message: error.message || 'Unknown error'
        });
      } finally {
        this.isCreating = false
      }
    }
  },
  mounted() {
    this.trackEvent('landing_page_viewed');
  }
}
</script>

<style>
/* CSS Reset */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  background: #000;
}

.landing-hero {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: clamp(6.5rem, 14vh, 9rem) clamp(1.25rem, 5vw, 4rem) 3rem;
  overflow-x: hidden;
  background:
    radial-gradient(circle at 76% 38%, rgba(255, 119, 0, 0.13), transparent 34%),
    linear-gradient(145deg, #030303 0%, #0b0703 56%, #130900 100%);
}

.hero-content {
  width: min(100%, 72rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.karaoke-headline {
  max-width: 15ch;
  margin: 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: clamp(2.5rem, 5.25vw, 5rem);
  font-weight: 800;
  letter-spacing: -0.015em;
  line-height: 1.16;
  text-wrap: balance;
}

.karaoke-line {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.karaoke-word {
  display: inline-block;
  margin: 0 0.11em;
  color: rgba(255, 255, 255, 0.4);
  animation-duration: 4.5s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.karaoke-word--1 {
  animation-name: karaoke-word-1;
}

.karaoke-word--2 {
  animation-name: karaoke-word-2;
}

.karaoke-word--3 {
  animation-name: karaoke-word-3;
}

.karaoke-word--4 {
  animation-name: karaoke-word-4;
}

.karaoke-word--5 {
  animation-name: karaoke-word-5;
}

@keyframes karaoke-word-1 {
  0%, 68.8% { color: #ff9d00; }
  73.3%, 100% { color: rgba(255, 255, 255, 0.4); }
}

@keyframes karaoke-word-2 {
  0%, 12.1% { color: rgba(255, 255, 255, 0.4); }
  12.2%, 68.8% { color: #ff9d00; }
  73.3%, 100% { color: rgba(255, 255, 255, 0.4); }
}

@keyframes karaoke-word-3 {
  0%, 24.3% { color: rgba(255, 255, 255, 0.4); }
  24.4%, 68.8% { color: #ff9d00; }
  73.3%, 100% { color: rgba(255, 255, 255, 0.4); }
}

@keyframes karaoke-word-4 {
  0%, 36.6% { color: rgba(255, 255, 255, 0.4); }
  36.7%, 68.8% { color: #ff9d00; }
  73.3%, 100% { color: rgba(255, 255, 255, 0.4); }
}

@keyframes karaoke-word-5 {
  0%, 48.8% { color: rgba(255, 255, 255, 0.4); }
  48.9%, 68.8% { color: #ff9d00; }
  73.3%, 100% { color: rgba(255, 255, 255, 0.4); }
}

.karaoke-progress {
  width: min(72vw, 36rem);
  height: 2px;
  margin-top: clamp(1.75rem, 4vw, 2.75rem);
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

.karaoke-progress-fill {
  width: 100%;
  height: 100%;
  background: #ff9d00;
  border-radius: inherit;
  transform: scaleX(0);
  transform-origin: left center;
  animation: karaoke-progress 4.5s linear infinite;
}

@keyframes karaoke-progress {
  0% { transform: scaleX(0); }
  48.9%, 68.8% { transform: scaleX(1); }
  73.3%, 100% { transform: scaleX(0); }
}

.start-button {
  min-width: 12rem;
  min-height: 3.5rem;
  margin-top: clamp(2.25rem, 5vw, 3.5rem);
  padding: 1rem 2.25rem;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #ff9d00;
  color: #090500;
  font: inherit;
  font-size: 1.05rem;
  font-weight: 750;
  cursor: pointer;
  box-shadow: 0 12px 32px rgba(255, 137, 0, 0.2);
  transition: background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.start-button:hover:not(:disabled) {
  background: #ffad29;
  box-shadow: 0 16px 38px rgba(255, 137, 0, 0.3);
  transform: translateY(-2px);
}

.start-button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 4px;
}

.start-button:disabled {
  cursor: not-allowed;
  opacity: 0.68;
}

.join-room-toggle {
  margin-top: 1rem;
  padding: 0.4rem 0.75rem;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font: inherit;
  font-size: 0.95rem;
  cursor: pointer;
}

.join-room-toggle:hover {
  color: #ff9d00;
}

.join-room-toggle:focus-visible,
.join-room-input:focus-visible,
.join-room-button:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.join-room-form {
  width: min(100%, 22rem);
  margin-top: 1rem;
  display: flex;
  gap: 0.6rem;
}

.join-room-input {
  min-width: 0;
  flex: 1;
  padding: 0.8rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font: inherit;
  text-transform: uppercase;
}

.join-room-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
  text-transform: none;
}

.join-room-button {
  padding: 0.8rem 1.25rem;
  border: 1px solid #ff9d00;
  border-radius: 999px;
  background: transparent;
  color: #ff9d00;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.join-room-button:hover {
  background: rgba(255, 157, 0, 0.12);
}

.join-room-error {
  margin-top: 0.75rem;
}

.error-message {
  max-width: 30rem;
  margin: 1.25rem 0 0;
  color: #ff8d8d;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.45;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.start-button .loading-spinner {
  border-color: rgba(0, 0, 0, 0.28);
  border-top-color: #000;
}

@media (max-width: 600px) {
  .landing-hero {
    padding: 5.75rem 1.25rem 2.5rem;
  }

  .karaoke-headline {
    max-width: 10ch;
    font-size: clamp(2.7rem, 14vw, 4.5rem);
    line-height: 1.02;
  }

  .karaoke-progress {
    width: min(82vw, 26rem);
    margin-top: 1.75rem;
  }

  .start-button {
    width: min(100%, 18rem);
    margin-top: 2.5rem;
  }

  .join-room-form {
    width: min(100%, 18rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .karaoke-word {
    animation: none;
  }

  .karaoke-word--1,
  .karaoke-word--2,
  .karaoke-word--3,
  .karaoke-word--4 {
    color: #fff;
  }

  .karaoke-word--5 {
    color: #ff9d00;
  }

  .karaoke-progress {
    display: none;
  }

  .start-button {
    transition: none;
  }

  .start-button:hover:not(:disabled) {
    transform: none;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.502);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin: 1rem;
  text-align: left;
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-content h2 {
  color: #ffa500;
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: 500;
}

.instructions-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.instruction-step h3 {
  color: white;
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
}

.instruction-step p {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media (max-width: 480px) {
  .modal-content {
    padding: 1.5rem;
    margin: 0.5rem;
    width: 95%;
    max-height: 85vh;
  }
  
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .instruction-step h3 {
    font-size: 1rem;
  }
  
  .instruction-step p {
    font-size: 0.875rem;
  }
}

.footer-links {
  margin-top: 1rem;
  text-align: center;
}

.footer-link {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-link:hover {
  color: rgba(255, 255, 255, 0.8);
}

.consent-list {
  list-style-type: none;
  margin: 1rem 0;
  padding: 0;
}

.consent-list li {
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.8);
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.4;
}

.consent-list li::before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #ffa500;
}

.consent-list a {
  color: #ffa500;
  text-decoration: none;
}

.consent-list a:hover {
  text-decoration: underline;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 4px;
  cursor: pointer;
}

.accept-button {
  padding: 0.5rem 1rem;
  background: #ffa500;
  border: none;
  color: white;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.accept-button:hover {
  background: #ff9500;
}

.accept-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.instructions-content p {
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.9);
}

.consent-modal h2 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
}

.consent-notice {
  background: rgba(255, 165, 0, 0.05);
  border-left: 4px solid #ffa500;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.consent-notice p {
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
}

.consent-notice strong {
  color: #ffa500;
  font-weight: 600;
}

.terms-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin: 0.75rem 0;
  transition: background 0.2s;
}

.terms-box:hover {
  background: rgba(255, 255, 255, 0.08);
}

.terms-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.terms-text {
  flex: 1;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;
}

.terms-text a,
.terms-text .router-link-active {
  color: #ffa500;
  text-decoration: none;
  font-weight: 500;
}

.terms-text a:hover {
  text-decoration: underline;
}

.consent-checkbox-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 2px solid rgba(255, 165, 0, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
}

.consent-checkbox-container:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 165, 0, 0.5);
}

.consent-checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #ffa500;
  flex-shrink: 0;
}

.consent-checkbox-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  cursor: pointer;
  user-select: none;
}
</style>
